'use client';

import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide';
import { useState } from 'react';
import '@splidejs/react-splide/css';
import ImageDetailModal from '@/components/ui/ImageDetailModal/ImageDetailModal';
import LoadingLink from '@/components/ui/LoadingLink/LoadingLink';
import NextImageView from '@/components/ui/NextImageView/NextImageView';
import RoundArrowIcon from '@/components/ui/RoundArrowIcon/RoundArrowIcon';
import SectionTitle from '@/components/ui/SectionTitle/SectionTitle';
import UserDesignDetailModal from '@/features/gfUserDesign/components/UserDesignDetailModal/UserDesignDetailModal';
import { SIZE_SELECT_FROM } from '@/features/gfUserDesign/constants';
import { useUserDesignSlider } from '@/hooks/useUserDesignSlider';
import utilsStyles from '@/styles/modules/utils.module.scss';
import type { UserDesignItem, UserDesignModalType } from '@/types';
import { USER_DESIGN_MODAL_TYPE } from '@/types';
import { getImageLayoutSizes } from '@/utils/nextImage';
import type { Route } from 'next';
import styles from './UserDesignsSlider.module.scss';

// 1スライドあたりの表示数
const PER_PAGE_SP = 3;
const PER_PAGE_PC = 12;

function UserDesignsSlider({
  userDesignList,
  modalType = USER_DESIGN_MODAL_TYPE.IMAGE_DETAIL,
}: {
  userDesignList: UserDesignItem[];
  modalType?: UserDesignModalType;
}) {
  const { isSp, slideItems, splideRef } = useUserDesignSlider(userDesignList, PER_PAGE_SP, PER_PAGE_PC);

  // 画像拡大モーダル用のstate
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState('');

  // デザイン詳細モーダル用のstate
  const [isDesignDetailModalOpen, setIsDesignDetailModalOpen] = useState(false);
  const [selectedItemIndex, setSelectedItemIndex] = useState(0);

  const spOptions = {
    type: 'loop',
    arrows: false,
    pagination: false,
    autoplay: false,
    clones: 0,
  };

  const pcOptions = {
    type: 'loop',
    perPage: 1,
    perMove: 1,
    autoplay: false,
    pagination: false,
    arrows: false,
    clones: 0,
  };

  // 画面サイズに応じたオプションを選択
  const splideOptions = isSp ? spOptions : pcOptions;

  // アイテムクリック時の処理
  const handleItemClick = (item: UserDesignItem) => {
    if (modalType === USER_DESIGN_MODAL_TYPE.IMAGE_DETAIL) {
      // 画像拡大モーダルを開く
      setSelectedImageUrl(item.previewImageUrl);
      setIsImageModalOpen(true);
    } else {
      // デザイン詳細モーダルを開く
      const originalIndex = userDesignList.findIndex((design) => design.id === item.id);
      setSelectedItemIndex(originalIndex);
      setIsDesignDetailModalOpen(true);
    }
  };

  // 画像拡大モーダルを閉じる処理
  const handleCloseImageModal = () => {
    setIsImageModalOpen(false);
    setSelectedImageUrl('');
  };

  // デザイン詳細モーダルを閉じる処理
  const handleCloseDesignDetailModal = () => {
    setIsDesignDetailModalOpen(false);
  };

  return (
    <>
      <div className={styles.userDesignsGridContainer}>
        <div className={styles.headerArea}>
          <SectionTitle englishLabel='USER DESIGNS' japaneseLabel='みんなのデザイン' />
          <p className={styles.userDesignsDescription}>
            みんなの<span className={styles.userDesignsAccent}>デザイン</span>から
            <br className={utilsStyles.onlySp} />
            オリジナルグッズをつくろう
          </p>
        </div>
        <div className={styles.goodsArea}>
          {slideItems.length > 0 && (
            <Splide
              key={isSp ? 'sp' : 'pc'}
              options={splideOptions}
              aria-label='User Designs'
              hasTrack={false}
              className={styles.splide}
              ref={splideRef}
            >
              <SplideTrack className={styles.splideTrack}>
                {slideItems.map((page) => (
                  <SplideSlide key={`slidePage-${page.id}`}>
                    <div className={styles.columnContainer}>
                      {page.items.map((item) => (
                        <div key={`slideItem-${item.id}`} className={styles.itemContent}>
                          <div className={styles.itemImageContainer} onClick={() => handleItemClick(item)}>
                            <NextImageView
                              src={item.previewImageUrl}
                              alt={item.gfWorkName}
                              fill
                              objectFit='contain'
                              sizes={getImageLayoutSizes({ sp: '33vw', pc: '160px' })}
                            />
                          </div>
                          <p className={styles.itemInfo}>{item.gfWorkName}</p>
                        </div>
                      ))}
                    </div>
                  </SplideSlide>
                ))}
              </SplideTrack>
            </Splide>
          )}
          {/* みんなのデザインをもっと見るボタン */}
          <div className={styles.viewMoreButtonContainer}>
            <LoadingLink href={'/user-designs' as Route} prefetch={false} className={styles.viewMoreButton}>
              <span className={styles.viewMoreButtonText}>
                <span className={utilsStyles.onlySp}>もっとみる</span>
                <span className={utilsStyles.onlyPc}>みんなのデザインをもっとみる</span>
              </span>
              <div className={styles.viewMoreButtonIcon}>
                <RoundArrowIcon direction='right' color='red' />
              </div>
            </LoadingLink>
          </div>
        </div>

        {/* モーダル表示 */}
        {modalType === USER_DESIGN_MODAL_TYPE.IMAGE_DETAIL ? (
          <ImageDetailModal
            isOpen={isImageModalOpen}
            onClose={handleCloseImageModal}
            imageUrl={selectedImageUrl}
            alt={selectedImageUrl}
          />
        ) : (
          <UserDesignDetailModal
            isOpen={isDesignDetailModalOpen}
            onClose={handleCloseDesignDetailModal}
            items={userDesignList}
            initialIndex={selectedItemIndex}
            getCallbackUrl={(item) => `/user-designs?openModal=${item.userPrintId}` as Route}
            from={SIZE_SELECT_FROM.userDesigns}
          />
        )}
      </div>
    </>
  );
}

export default UserDesignsSlider;
